import * as React from 'react';

import { Badge, CheckboxGroup, Checkbox, TextField } from '@rematter/ui-web';

import { useTranslations } from '../../i18n/useTranslations';
import {
  getAllToolIds,
  getToolCategory,
  getToolDescription,
  getToolDisplayName,
  ToolId,
} from '../../utils/toolRegistry';

interface ToolSelectorProps {
  selectedToolIds: string[];
  onChange: (ids: string[]) => void;
}

export const ToolSelector: React.FC<ToolSelectorProps> = ({
  selectedToolIds,
  onChange,
}) => {
  const t = useTranslations();
  const allTools = getAllToolIds();
  const [query, setQuery] = React.useState('');

  // Group tools by category
  const toolsByCategory = React.useMemo(() => {
    const grouped: Record<string, ToolId[]> = {};
    const q = query.trim().toLowerCase();
    allTools.forEach((toolId) => {
      const displayName = getToolDisplayName(toolId, t) ?? toolId;
      const description = getToolDescription(toolId, t) ?? '';
      const haystack = `${displayName} ${description} ${toolId}`.toLowerCase();
      if (q && !haystack.includes(q)) return;

      const category =
        getToolCategory(toolId, t) ?? t.aiManagement.tools.categories.other;
      if (!grouped[category]) {
        grouped[category] = [] as ToolId[];
      }
      grouped[category].push(toolId as ToolId);
    });
    return grouped;
  }, [allTools, t, query]);

  const handleClick = (toolId: string) => {
    const isSelected = selectedToolIds.includes(toolId);
    if (isSelected) {
      onChange(selectedToolIds.filter((id) => id !== toolId));
    } else {
      onChange([...selectedToolIds, toolId]);
    }
  };

  return (
    <div className="@ToolSelector flex flex-col gap-6">
      <TextField
        aria-label={t.aiManagement.agents.details.fields.tools.placeholder}
        placeholder={t.aiManagement.agents.details.fields.tools.placeholder}
        value={query}
        onChange={setQuery}
      />

      {Object.entries(toolsByCategory).map(([category, tools]) => {
        if (!tools.length) return null;
        return (
          <div key={category} className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-neutral/70">{category}</h3>
            <CheckboxGroup value={selectedToolIds} onChange={onChange}>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {tools.map((toolId) => {
                  const displayName = getToolDisplayName(toolId, t);
                  const description = getToolDescription(toolId, t);

                  return (
                    <div
                      key={toolId}
                      className="grid cursor-pointer grid-cols-12 grid-rows-1 items-start gap-3 rounded border border-neutral/10 p-3 hover:bg-neutral/5"
                      onClick={() => handleClick(toolId)}
                    >
                      <Checkbox value={toolId} className="col-span-1 mt-0.5" />
                      <div className="col-span-11 flex flex-col gap-1">
                        <span className="font-medium text-neutral/90">
                          {displayName}
                        </span>
                        <span>
                          <Badge color="neutral" size="small">
                            {toolId}
                          </Badge>
                        </span>
                        {description && (
                          <p className="text-sm text-neutral/60">
                            {description}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CheckboxGroup>
          </div>
        );
      })}
    </div>
  );
};
